import React, {useEffect} from 'react'
import { observer, inject } from "mobx-react"

import { Link } from "gatsby"
import Helmet from "react-helmet";

import Layout from "../components/layout"
import Seo from "../components/seo"
import Section from "../components/template-partials/global-components/section"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { faExternalLinkAlt, faDownload } from "@fortawesome/free-solid-svg-icons";
import nbinfo from "../images/nbinfo.jpg";
import doctorNurse from "../images/doctorNurse.svg";
import planner from "../images/planner.svg";
import journal from "../images/journal.svg";
import care from "../images/care.svg";
import help from "../images/help.svg";

import parentBrochure from "../images/parent_brochure.jpg";
import caregiverJournal from "../images/caregiver_journal.jpg";
import utxColorIcon from "../images/utx-color-icon.svg";
import video from "../images/video-icon.svg";

const ResourcesCgPage = inject(`store`)(observer(({ store }) => {
  useEffect(()=>{
    store.hcpStore.updateProp('isHcp', false);
  },[]);
  return (
    <Layout>
      <Helmet>
        <script data-react-helmet="true">{`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','GTM-NJ4Q8RV');`}</script>
        <body className="cg-layout" />
      </Helmet>
      <Seo title="Neuroblastoma Support and Resources" description="Find resources to help you and your loved ones on the neuroblastoma treatment journey, including support organizations and helpful downloads. Please see Important Safety Information and full Prescribing Information on this website." ogDescription="Find neuroblastoma support groups, tips, and more. Please see Important Safety Information and full Prescribing Information on this website." />
      <div className="page-heading">
        <div className="row">
          <div className="columns">
            <h1 className="page-heading__heading">Resources</h1>
          </div>
        </div>
      </div>

      <Section>
        <div className="row">
          <div className="columns">
            <h2 className="h4 text--blue">Find resources to help you on your neuroblastoma treatment journey.</h2>
            <ul className="standard link-list">
              <li><a href="#nbinfo" data-gtm-event-category="Internal Anchor Link" data-gtm-event-action="Link Text">Navigate your neuroblastoma journey at neuroblastoma-info.com</a></li>
              <li><a href="#tips" data-gtm-event-category="Internal Anchor Link" data-gtm-event-action="Link Text">Review helpful tips for parents and caregivers</a></li>
              <li><a href="#downloads" data-gtm-event-category="Internal Anchor Link" data-gtm-event-action="Link Text">Download resources for treatment with Unituxin®</a></li>
              <li><a href="#support" data-gtm-event-category="Internal Anchor Link" data-gtm-event-action="Link Text">Find support through advocacy and research organizations</a></li>
            </ul>
          </div>
        </div>
      </Section>

      <Section id="nbinfo">
        <div className="row">
          <div className="columns">
            <h2 className="section__heading h3">Navigating your neuroblastoma treatment journey</h2>
          </div>
        </div>
        <div className="row lg_mt-2 mt-2 align-middle">
          <div className="columns large-4 small-12">
            <img src={nbinfo} alt="Neuroblastoma-info.com"/>
          </div>
          <div className="columns mt-2">
            <p><strong className="text--blue">Neuroblastoma-info.com: Neuroblastoma support, every step of the way. </strong></p>
            <p>From diagnosis to treatment and beyond, at neuroblastoma-info.com you’ll find the information you need to help guide you and your child along their treatment journey. No matter where you and your family are in your neuroblastoma journey, you can find resources and support at neuroblastoma-info.com.</p>
            <p>Neuroblastoma-info.com is sponsored by United Therapeutics Corporation.</p>
            <p>
              <a href="https://www.neuroblastoma-info.com" target="_blank" data-gtm-event-category="External Link" data-gtm-event-action="Link URL" rel="noreferrer" className="button button--ext">Find neuroblastoma resources <br className="hide-for-large"/> and support <FontAwesomeIcon icon={faExternalLinkAlt}/></a>
            </p>
          </div>
        </div>
      </Section>

      <Section id="tips">
        <div className="row">
          <div className="columns">
            <h2 className="section__heading h3">Helpful tips for parents and caregivers</h2>
            <div className="section__indent lg_mt-1 mt-1">
              <p><strong>As you adjust to the routine of antibody therapy,</strong> you may find different ways to help manage the journey. Below are a few tips that other families have found useful.</p>
            </div>
          </div>
        </div>

        <div className="row lg_mt-2 mt-2 align-middle">
          <div className="columns small-12 text-center large-shrink">
            <div className="section__indent">
              <img src={doctorNurse} style={{minWidth:"100px"}} alt="speak up"/>
            </div>
          </div>
          <div className="columns mt-2">
            <p>
              <strong className="text--blue">Speak up</strong><br/>
              As a parent or caregiver, no one knows your child better than you. You have an important role in helping the healthcare team manage your child’s care. Always pay close attention to any changes in your child’s condition, and tell your healthcare team immediately if you notice any changes or are concerned.
            </p>
          </div>
        </div>

        <div className="row lg_mt-3 mt-3 align-middle">
          <div className="columns small-12 text-center large-shrink">
            <div className="section__indent">
              <img src={planner} style={{minWidth:"100px"}} alt="Use a calendar"/>
            </div>
          </div>
          <div className="columns mt-2">
            <p>
              <strong className="text--blue">Use a calendar</strong><br/>
              Cycles of antibody therapy may vary. Following a treatment calendar can help you keep track of the busy schedule. Understanding the order of events in each cycle can help you to plan ahead before clinic appointments and hospital admissions. It may also help you to anticipate when you may need to take time off work and schedule childcare for siblings.
            </p>
          </div>
        </div>

        <div className="row lg_mt-3 mt-3 align-middle">
          <div className="columns small-12 text-center large-shrink">
            <div className="section__indent">
              <img src={journal} style={{minWidth:"100px"}} alt="Keep a journal"/>
            </div>
          </div>
          <div className="columns mt-2">
            <p>
              <strong className="text--blue">Keep a journal</strong><br/>
              It may be helpful to record notes about medications and other information to help you better understand the regimen and your child’s progress during each cycle. <strong>Please see the Caregiver Journal download below.</strong>
            </p>
          </div>
        </div>

        <div className="row lg_mt-3 mt-3 align-middle">
          <div className="columns small-12 text-center large-shrink">
            <div className="section__indent">
              <img src={care} style={{minWidth:"100px"}} alt="Take care of your family"/>
            </div>
          </div>
          <div className="columns mt-2">
            <p>
              <strong className="text--blue">Take care of your family</strong><br/>
              It is not uncommon for siblings to have a difficult time understanding why their brother or sister is sick. Siblings can often feel forgotten and may need additional attention and support because the child with cancer requires so much focus. <a href="https://www.alexslemonade.org/childhood-cancer/for-families/supersibs" target="_blank" data-gtm-event-category="External Link" data-gtm-event-action="Link URL">For more information on helping siblings cope, you can visit the SuperSibs page of the Alex’s Lemonade Stand website <FontAwesomeIcon icon={faExternalLinkAlt}/></a>
            </p>
          </div>
        </div>

        <div className="row lg_mt-3 mt-3 align-middle">
          <div className="columns small-12 text-center large-shrink">
            <div className="section__indent">
              <img src={help} style={{minWidth:"100px"}} alt="Ask for help"/>
            </div>
          </div>
          <div className="columns mt-2">
            <p>
              <strong className="text--blue">Ask for help</strong><br/>
              Caring for a child with cancer can affect you physically as well as emotionally. It is important to reach out to friends and family for support during this time. Be specific about what you need. Sometimes help with chores can be just as valuable as a listening ear.
            </p>
          </div>
        </div>

      </Section>

      <Section id="downloads">
        <div className="row">
          <div className="columns">
            <h2 className="section__heading h3">Support resources for Unituxin treatment</h2>
          </div>
        </div>
        <div className="row lg_mt-3 mt-3 align-top">
          <div className="columns shrink text-center">
            <div className="section__indent">
              <img src={parentBrochure} style={{maxWidth: '135px'}} alt="Parent Brochure"/>
            </div>
          </div>
          <div className="columns large-8">
            <p className="text--blue h5"><strong>Parent Brochure</strong></p>
            <p>Find answers to some of your questions, information about Unituxin treatment, and helpful resources in the booklet <em>Understanding Antibody Therapy: Treatment for Pediatric High-Risk Neuroblastoma</em>.</p>
            <p><a href="/US-UTX-0111-Unituxin-Parent-Book-1-24-20.pdf" target="_blank" data-gtm-event-category="PDF Download" data-gtm-event-action="Link URL"  rel="noreferrer" className="button button--ext no-ext">Download PDF <FontAwesomeIcon icon={faDownload}/></a></p>
          </div>
        </div>
        <div className="row lg_mt-3 mt-3 align-top">
          <div className="columns shrink text-center">
            <div className="section__indent">
              <img src={caregiverJournal} style={{maxWidth: '135px'}} alt="Caregiver Journal"/>
            </div>
          </div>
          <div className="columns large-8">
            <p className="text--blue h5"><strong>Caregiver Journal</strong></p>
            <p>Use the journal to keep track of important notes during your child's treatment journey and to keep a list of the members of their healthcare team.</p>
            <p><a href="/unituxin-caregiver-journal.pdf" target="_blank" rel="noreferrer" className="button button--ext no-ext" data-gtm-event-category="PDF Download" data-gtm-event-action="Link URL">Download PDF <FontAwesomeIcon icon={faDownload}/></a></p>
          </div>
        </div>
      </Section>

      <Section id="support">
        <div className="row">
          <div className="columns">
            <h2 className="section__heading h3">Advocacy and research organizations </h2>
            <div className="section__indent lg_mt-1 mt-1">
              <p>The groups below provide helpful information and support for families like yours that are coping with a childhood cancer diagnosis.</p>
            </div>
          </div>
        </div>
        <div className="row lg_mt-2 mt-2">
          <div className="columns">
            <div className="section__indent">
              <h3 className="text--blue h5">Advocacy groups</h3>
              <p className="lg_mt-2 mt-2">
                <a href="https://www.cncfhope.org/" data-gtm-event-category="External Link" data-gtm-event-action="Link URL" target="_blank" rel="noreferrer"> <strong>Children's Neuroblastoma Cancer Foundation (CNCF)</strong> <FontAwesomeIcon icon={faExternalLinkAlt}/></a><br/>
                CNCF provides educational materials about neuroblastoma and treatment, news and information about events, and links to oncologists and researchers who specialize in treating neuroblastoma.
              </p>
              <p className="lg_mt-2 mt-2">
                <a href="https://www.alexslemonade.org/" data-gtm-event-category="External Link" data-gtm-event-action="Link URL" target="_blank" rel="noreferrer"> <strong>Alex’s Lemonade Stand Foundation (ALSF) </strong> <FontAwesomeIcon icon={faExternalLinkAlt}/></a><br/>
                ALSF funds impactful research, raises awareness, supports families, and empowers everyone to help cure childhood cancer.
              </p>
            </div>
          </div>
        </div>
        <div className="row lg_mt-2 mt-2">
          <div className="columns">
            <div className="section__indent">
              <h3 className="text--blue h5">Research groups</h3>
              <p className="lg_mt-2 mt-2">
                <a href="https://childrensoncologygroup.org/" data-gtm-event-category="External Link" data-gtm-event-action="Link URL" target="_blank" rel="noreferrer"> <strong>The Children’s Oncology Group (COG)</strong> <FontAwesomeIcon icon={faExternalLinkAlt}/></a><br/>
                COG is a clinical trials group supported by the National Cancer Institute and is the world’s largest organization devoted exclusively to childhood and adolescent cancer research.
              </p>
              <p className="lg_mt-2 mt-2">
                <a href="https://www.nant.org/" data-gtm-event-category="External Link" data-gtm-event-action="Link URL" target="_blank" rel="noreferrer"> <strong>New Approaches to Neuroblastoma Therapy (NANT) </strong> <FontAwesomeIcon icon={faExternalLinkAlt}/></a><br/>
                NANT is a research group focused solely on developing new treatments and biomarkers for relapsed/refractory high-risk neuroblastoma.
              </p>
              <p className="lg_mt-2 mt-2">
                <a href="https://beatcc.org/foundation/" data-gtm-event-category="External Link" data-gtm-event-action="Link URL" target="_blank" rel="noreferrer"> <strong>Beat Childhood Cancer </strong> <FontAwesomeIcon icon={faExternalLinkAlt}/></a><br/>
                Beat Childhood Cancer is a research foundation dedicated to finding viable treatments and, ultimately, cures for childhood cancers.
              </p>
            </div>
          </div>
        </div>
      </Section>

      <Section className="section--orange-grad">
        <div className="row card-row">
          <div className="columns small-12 medium-6">
            <Link to="/why-unituxin/" className="card card--horiz card--arrow-orange" data-gtm-event-category="Internal Link" data-gtm-event-action="Link URL">
              <div className="card__image text-center" data-gtm-event-label="/why-unituxin/" data-gtm-event-category="Internal Link" data-gtm-event-action="Link URL">
                <img src={utxColorIcon} alt="Unituxin Color Icon" data-gtm-event-label="/why-unituxin/" data-gtm-event-category="Internal Link" data-gtm-event-action="Link URL"/>
              </div>
              <div className="card__text" data-gtm-event-label="/why-unituxin/" data-gtm-event-category="Internal Link" data-gtm-event-action="Link URL">
                <div className="card__heading" data-gtm-event-label="/why-unituxin/" data-gtm-event-category="Internal Link" data-gtm-event-action="Link URL">Why Unituxin?</div>
                <p data-gtm-event-label="/why-unituxin/" data-gtm-event-category="Internal Link" data-gtm-event-action="Link URL">See why doctors and pediatric cancer professionals choose Unituxin. </p>
              </div>
            </Link>
          </div>
          <div className="columns small-12 medium-6 mt-2">
            <Link to="/how-unituxin-works/#video" target="_blank" data-gtm-event-category="Internal Anchor Link" data-gtm-event-action="Link URL" rel="noreferrer" className="card card--horiz no-ext card--arrow-orange">
              <div className="card__image text-center" data-gtm-event-label="/how-unituxin-works/#video" data-gtm-event-category="Internal Anchor Link" data-gtm-event-action="Link URL">
                <img src={video} alt="medical book graphic" data-gtm-event-label="/how-unituxin-works/#video" data-gtm-event-category="Internal Anchor Link" data-gtm-event-action="Link URL"/>
              </div>
              <div className="card__text" data-gtm-event-label="/how-unituxin-works/#video" data-gtm-event-category="Internal Anchor Link" data-gtm-event-action="Link URL">
                <div className="card__heading" data-gtm-event-label="/how-unituxin-works/#video" data-gtm-event-category="Internal Anchor Link" data-gtm-event-action="Link URL">How does Unituxin work?</div>
                <p data-gtm-event-label="/how-unituxin-works/#video" data-gtm-event-category="Internal Anchor Link" data-gtm-event-action="Link URL">Learn about antibody therapy and watch a video on how it works.</p>
              </div>
            </Link>
          </div>
        </div>
      </Section>

    </Layout>
  );
}));

export default ResourcesCgPage
